import React, { useEffect, useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { Clock, Shirt } from "lucide-react";

export default function ContentSection({ section }) {
  const { eventId } = useAdminEvent();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      let d = [];
      if (section === "hotel-dining") {
        const [h, r] = await Promise.all([api.hotels(eventId), api.restaurants(eventId)]);
        d = { hotels: h, restaurants: r };
      } else if (section === "activities") d = await api.activities(eventId);
      else if (section === "photos") d = await api.photos(eventId);
      else if (section === "faqs") d = await api.faqs(eventId);
      else if (section === "kbyg") { const r = await api.kbyg(eventId); d = r.items || []; }
      else if (section === "contacts") d = await api.contacts(eventId);
      if (active) setData(d);
    };
    load();
    return () => { active = false; };
  }, [section, eventId]);

  const intros = {
    "hotel-dining": "The resort and its dining venues shown to attendees in Explore.",
    activities: "Curated activities available at the resort.",
    photos: "Event gallery shown in the attendee Photos tab.",
    faqs: "Frequently asked questions shown to attendees.",
    contacts: "On-site and emergency contacts available to attendees.",
    kbyg: "Know Before You Go guidance shown to attendees, personalized by group.",
  };

  return (
    <div>
      <PageIntro>{intros[section]}</PageIntro>

      {section === "kbyg" && Array.isArray(data) && (
        <div className="space-y-2">
          {data.map((k) => (<div key={k.id} className="premium-card p-4"><div className="text-[10px] uppercase font-semibold text-[hsl(var(--pc-gold))]">{k.category}</div><div className="font-semibold text-[hsl(var(--primary))]">{k.title}</div><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{k.content}</p></div>))}
          {data.length === 0 && <div className="premium-card p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No Know Before You Go content yet.</div>}
        </div>
      )}

      {section === "hotel-dining" && data && (
        <div className="space-y-6">
          {data.hotels?.map((h) => (
            <div key={h.id} className="premium-card overflow-hidden">
              <img src={h.image} alt={h.name} className="h-40 w-full object-cover" />
              <div className="p-4"><h3 className="font-serif text-2xl text-[hsl(var(--primary))]">{h.name}</h3><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{h.description}</p></div>
            </div>
          ))}
          <div className="grid sm:grid-cols-2 gap-3">
            {data.restaurants?.map((r) => (
              <div key={r.id} className="premium-card overflow-hidden flex">
                <img src={r.image} alt="" className="h-24 w-24 object-cover" />
                <div className="p-3"><div className="text-[10px] uppercase font-semibold text-[hsl(var(--pc-gold))]">{r.cuisine}</div><div className="font-semibold text-[hsl(var(--primary))]">{r.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))] flex gap-2 mt-1"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{r.hours}</span></div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === "activities" && Array.isArray(data) && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.map((a) => (
            <div key={a.id} className="premium-card overflow-hidden"><img src={a.image} alt="" className="h-28 w-full object-cover" /><div className="p-3"><div className="text-[10px] uppercase font-semibold text-[hsl(var(--pc-gold))]">{a.category}</div><div className="font-semibold text-[hsl(var(--primary))]">{a.name}</div></div></div>
          ))}
        </div>
      )}

      {section === "photos" && Array.isArray(data) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {data.map((p) => (<img key={p.id} src={p.url} alt={p.caption} className="aspect-square w-full rounded-xl object-cover" />))}
        </div>
      )}

      {section === "faqs" && Array.isArray(data) && (
        <div className="space-y-2">
          {data.map((f) => (<div key={f.id} className="premium-card p-4"><div className="font-semibold text-[hsl(var(--primary))]">{f.question}</div><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{f.answer}</p></div>))}
        </div>
      )}

      {section === "contacts" && Array.isArray(data) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.map((c) => (<div key={c.id} className="premium-card p-4"><div className="font-semibold text-[hsl(var(--primary))]">{c.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{c.role}</div><div className="text-sm text-[hsl(var(--primary))] mt-2">{c.phone}</div>{c.email && <div className="text-sm text-[hsl(var(--muted-foreground))]">{c.email}</div>}</div>))}
        </div>
      )}

      {!data && <div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />}
    </div>
  );
}
